import React, { useState, FC, useEffect } from 'react'
import t from 'src/translations/main'
import Icon from 'src/components/Core/Iconography/Icon/Icon'
import Font from 'src/components/Core/Typography/Font/Font'
import {
  Title,
  NewThreadBox,
  CircleButtonWrapper,
  MainPanelWrapper,
  NewThreadButtons,
  ButtonsWrapper
} from './OnboardingChatPanel.styles'
import { OnboardingChatPanelProps } from '../types'
import Input from 'src/components/Atoms/Input/Input'
import Button from 'src/components/Atoms/Button/Button'
import ChatComments from './ChatComments'
import { Comments } from './types'
import useFetch from 'src/components/Core/Hooks/useFetch/useFetch'
import { getBaseApiHost } from 'src/components/Core/Utils/url_constants'
import storage from 'src/components/Core/Utils/storage'
import { useParams } from 'react-router-dom'
import Popup from 'src/components/Atoms/Popup/Popup'
import updateModal from 'src/components/Sections/Modal/updateModal'

const OnboardingChatPanel: FC<OnboardingChatPanelProps> = props => {
  const [isOpen, setOpen] = useState(false)
  const [comments, setMessages] = useState<Comments[]>([])
  const [generalError, setGeneralError] = useState({error: false, message: ''})
  const [approvingOrReverting, setApproveOrReverting] = useState('')

  const { flowKey, step } = useParams()
  const { sendRequest } = useFetch()

  const {
    userPermission,
    stepName,
    applicationType,
    setOptionsList,
    setRefetch,
    enableReverse,
    setEnableReverse
  } = props

  const userName: string = storage.get(storage?.keys?.main?.user)?.name
  const applicationId: string = storage.get(storage?.keys?.onboarding?.applicationId)

  useEffect(() => {
    sendRequest({
      scope: 'onboarding',
      url: import.meta.env.VITE_MOCK_DATA !== 'true'
        ? `${getBaseApiHost()}/Comments/getcomments?applicationId=${applicationId}&stepIndex=${step}`
        : `${window.location.origin}/mock/comments-get-form-step-${parseInt(step) > 6 ? 100 : step}.json`,
    },
    data => {
      setMessages(data)
      if(data?.length > 0){
        setOpen(true)
        openPanelMarkMessages(data)
      }
    })

    return () => setOpen(false)
  },[step])

  // @ts-ignore:next-line
  const sendComment = (event, parentId?: number, isReply?: boolean) => {
    // @ts-ignore:next-line
    const textValue = event.target?.parentElement?.previousSibling?.children[0]?.children[0]?.value
    if(!textValue || textValue === ''){
      setGeneralError({error: true, message: t('onboarding.chat.popUpMessages.emptyComment')})
      return
    }
    const payload = {
      username: userName,
      stepIndex: step,
      parentCommentid: isReply ? parentId : 0,
    }

    const postOpts = {
      applicationID: applicationId,
      commentContent: textValue || '',
      fileBase64Contents: ''
    }

    clearTextInput(event)

    const modifyResponse = (data: { commentID: number, fileID: number }) => {
      if(data) {
        if(isReply) {
          const parentCommentIndex = comments.findIndex(comment => comment.commentID === parentId)
          const childComments = comments[`${parentCommentIndex}`].childcomments
          const addChildComment = [...childComments, {
            ...payload,
            commentID: data.commentID,
            commentContent: postOpts.commentContent,
            fileID: 0,
            fileName: '',
            isCommentRead: 'true',
            commentDateTime: new Date(),
            self: true,
          }]
          setMessages(prev => {
            prev[`${parentCommentIndex}`].childcomments = addChildComment
            return prev
          })
        } else {
          setMessages(prev => {
            const temp = [...prev]
            temp.push({
              ...payload,
              commentID: data.commentID,
              commentContent: postOpts.commentContent,
              fileID: 0,
              fileName: '',
              isCommentRead: 'true',
              commentDateTime: new Date(),
              self: true,
              childcomments: [] //extra
            })
            return temp
          })
        }
        setOptionsList(prevList => (
          prevList?.map((obj, index) => {
            if (index === (parseInt(step) - 1)) {
              obj.commentsCount = obj.commentsCount + 1
            }
            return obj
          })
        ))
        setEnableReverse(true)
      }
    }

    sendRequest({
      scope: 'onboarding',
      url: import.meta.env.VITE_MOCK_DATA !== 'true'
        ? `${getBaseApiHost()}/Comments/addComment`
        : `${window.location.origin}/mock/comments-post-withoutFile.json`,
      method: 'POST',
      body: {...payload, ...postOpts, filename: ''}
    },
    modifyResponse
    )
  }

  const clearTextInput = (event: React.MouseEvent<HTMLButtonElement>) => {
    // @ts-ignore:next-line
    event.target.parentElement.previousSibling.children[0].children[0].value = ''
  }

  const openPanelMarkMessages = (data?: Comments[]) => {
    setOpen(true)
    if(!data || data.length === 0) return
    const dataToFilter = data || comments
    const unreadCommentsId = dataToFilter.filter(comment => comment.isCommentRead?.toLowerCase?.() === 'false').map(unreadComment => unreadComment?.commentID)
    if(unreadCommentsId.length > 0) {
      sendRequest({
        scope: 'onboarding',
        url: import.meta.env.VITE_MOCK_DATA !== 'true'
          ? `${getBaseApiHost()}/Comments/markcommentasread`
          : `${window.location.origin}/mock/postMessage.json`,
        method: 'POST',
        body: {
          applicationID: applicationId,
          commentIds: unreadCommentsId
        }
      },
      data => {
        if(data) {
          setOptionsList(prevList => (
            prevList?.map((obj, index) => {
              if (index === parseInt(step) - 1) {
                obj.unreadCommentsCount = obj.unreadCommentsCount - unreadCommentsId.length
              }
              return obj
            })
          ))
          setMessages(prev => {
            const temp = [...prev]
            temp.forEach(comment => {
              if(comment?.isCommentRead?.toLowerCase() === 'false'){
                comment.isCommentRead = 'true'
              }
            })
            return temp
          })
        }
      }
      )
    }
  }

  const approveRevert = (action: string) => {
    setApproveOrReverting(action)
    sendRequest(
      {
        scope: 'onboarding',
        url: import.meta.env.VITE_MOCK_DATA !== 'true'
          ? `${getBaseApiHost()}/${applicationType}/update${applicationType}Status?applicationId=${applicationId}&status=${action}`
          : `${window.location.origin}/mock/postMessage.json`,
        method: 'POST',
      },
      data => {
        if(data) {
          setApproveOrReverting('')
          if(data.reasonPhrase) {
            setGeneralError({error: true, message: t('onboarding.chat.popUpMessages.errorUpdate')})
          } else {
            setRefetch(prev => !prev)
          }
        }
      }
    )
  }

  const callRevert = (e: React.MouseEvent<HTMLDivElement, MouseEvent>) => {
    if(!enableReverse) {
      e.preventDefault()
      setGeneralError({error: true, message: t('onboarding.chat.popUpMessages.submitCommentRevert')})
      return
    }
    approveRevert('revert')
  }

  const isEmployeeDocumentation = userPermission === 'Documentation'

  return (
    <>
      <MainPanelWrapper isOpen={isOpen} justifyContent='flex-start' alignItems='flex-start' flexDirection='column'>
        <Title justifyContent='space-between' maxWidth>
          <Font tag='h3'>{t('onboarding.chat.title')}</Font>
          <Icon name='close' size='xs' color='dark' clickFunction={() => setOpen(false)}/>
        </Title>
        {
          isEmployeeDocumentation &&
            <NewThreadBox flexDirection='column' alignItems='flex-start' maxWidth>
              <Font tag='h3' margin='0 0 10px 0'>{t(`onboarding.forms.${flowKey}.${stepName}.title`)}</Font>
              <Input placeholder={t('onboarding.chat.newThreadInputTextArea')} isTextarea minHeight='0'/>
              <NewThreadButtons gap='8px' justifyContent='flex-end' maxWidth>
                {/* @ts-ignore:next-line */}
                <Button label={t('onboarding.chat.buttons.clear')} version={2} fontSize='14px' onClick={clearTextInput}/>
                {/* @ts-ignore:next-line */}
                <Button label={t('onboarding.chat.buttons.submit')} fontSize='14px' onClick={sendComment}/>
              </NewThreadButtons>
            </NewThreadBox>
        }
        <ChatComments
          comments={comments}
          info={{applicationId, step}}
          setMessages={setMessages}
          clearTextInput={clearTextInput}
          setOptionsList={setOptionsList}
          sendComment={sendComment}
        />
      </MainPanelWrapper>
      {
        !isOpen && (
          <ButtonsWrapper flexDirection='column' gap='8px'>
            {
              isEmployeeDocumentation
              && (storage.get(storage?.keys?.onboarding?.applicationStatus) === 'submitted' || storage.get(storage?.keys?.onboarding?.applicationStatus) === 'signature_received')
              &&
              <>
                <CircleButtonWrapper
                  onClick={
                    () => updateModal({
                      title: t('onboarding.chat.approveRevert.title'),
                      modalState: 'open',
                      content: (
                        <Font>{t('onboarding.chat.approveRevert.approveModalText')}</Font>
                      ),
                      footerActions: [
                        {
                          label: t('onboarding.chat.approveRevert.yes'),
                          onClick: () => {
                            approveRevert('approve')
                            updateModal({ modalState: 'close' })
                          }
                        },
                        {
                          label: t('onboarding.chat.approveRevert.cancel'),
                          onClick: () => updateModal({ modalState: 'close' })
                        }
                      ],
                      fixedWidthButton: '60px'
                    })
                  }
                  bgColor='green'
                  border={true}
                >
                  <Icon name='check' size='s' color='blank' />
                </CircleButtonWrapper>
                <CircleButtonWrapper onClick={callRevert} bgColor='warning' disabledButton={!enableReverse}>
                  <Icon name='renew' size='s' color='blank' />
                </CircleButtonWrapper>
              </>
            }
            <CircleButtonWrapper onClick={() => openPanelMarkMessages()}>
              <Icon name={isEmployeeDocumentation ? 'chat-plus' : 'dataViewAlt'} size='s' color='blank' />
            </CircleButtonWrapper>
          </ButtonsWrapper>
        )
      }
      {
        generalError.error &&
        <Popup
          label={generalError.message}
          popupCloseHelperFunction={() => setGeneralError({error: false, message: ''})}
          click={() => setGeneralError({error: false, message: ''})}
          type='error'
        />
      }
      {
        approvingOrReverting &&
        <Popup
          label={
            approvingOrReverting === 'approve'
              ? t('onboarding.chat.popUpMessages.approvingApplication')
              : t('onboarding.chat.popUpMessages.revertingApplication')
          }
          type='warning'
        />
      }
    </>
  )
}

export default OnboardingChatPanel
